import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { FiPlus } from "react-icons/fi";
import { theme } from "../../styles/theme";
import logo from "../../assets/logo.jpg";
import SubmitModal from "./SubmitModal";
import SeedsPanel from "./SeedsPanel";
import ProjectsPanel from "./ProjectsPanel";
import VideosPanel from "./VideosPanel";
import ActorsPanel from "./ActorsPanel";
import SeedSliceDialog from "./SeedSliceDialog";
import RemixDialog from "./RemixDialog";
import RenderDialog from "./RenderDialog";
import ModifyPromptDialog from "./ModifyPromptDialog";
import InitialRemixDialog from "./InitialRemixDialog";
import RetryDialog from "./RetryDialog";
import VideoDupeDialog from "./VideoDupeDialog";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { useDataStreams } from "../../hooks/useDataStreams";
import {
  createProject,
  deleteProject,
  deleteVersion,
  deleteSeed,
  deleteVideo,
  toggleSeedStar,
  retryProject,
  retryVideo,
  dupeVideo,
  copyVideo,
} from "../../services/api";

const DashboardCanvas = styled.div`
  min-height: 100vh;
  background-color: ${theme.colors.background.canvas};
`;

const Shell = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${theme.spacing.xl} ${theme.spacing.md} 120px;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`;

const TopBar = styled.header`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding-bottom: ${theme.spacing.md};
  border-bottom: ${theme.borders.base};
  position: sticky;
  top: 0;
  z-index: 5;
  background: ${theme.colors.background.canvas};
`;

const LogoLockup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const LogoMark = styled.img`
  width: 48px;
  height: 48px;
  border-radius: ${theme.borderRadius.lg};
  object-fit: cover;
`;

const TitleStack = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.1;
`;

const Tabs = styled.div`
  display: inline-flex;
  gap: ${theme.spacing.md};
  flex-wrap: wrap;
  justify-content: center;
`;

const TabButton = styled.button`
  border: none;
  background: transparent;
  color: ${({ active }) =>
    active ? theme.colors.text.primary : theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${({ active }) =>
    active ? theme.typography.fontWeight.bold : theme.typography.fontWeight.normal};
  padding: ${theme.spacing.xs} 0;
  cursor: pointer;
  text-align: center;
  border-bottom: 2px solid ${({ active }) => (active ? theme.colors.text.primary : "transparent")};
`;

const StickyButton = styled(Button)`
  position: fixed;
  bottom: ${theme.spacing.lg};
  right: ${theme.spacing.lg};
  z-index: 20;
`;

const Banner = styled.div`
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.status.error}11;
  color: ${theme.colors.status.error};
  border: 1px solid ${theme.colors.status.error}33;
`;

const ContentSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const tabs = [
  { id: "seeds", label: "Seeds" },
  { id: "projects", label: "Projects" },
  { id: "videos", label: "Videos" },
  { id: "actors", label: "Actors" },
];

function Dashboard() {
  const { seeds, projects, videos, loading, error } = useDataStreams();
  const [activeTab, setActiveTab] = useState("projects");
  const [isSubmitOpen, setSubmitOpen] = useState(false);
  const [sliceSeed, setSliceSeed] = useState(null);
  const [sliceBusy, setSliceBusy] = useState(false);
  const [initialRemixSeed, setInitialRemixSeed] = useState(null);
  const [initialSlice, setInitialSlice] = useState(null);
  const [remixTarget, setRemixTarget] = useState(null);
  const [renderTarget, setRenderTarget] = useState(null);
  const [modifyTarget, setModifyTarget] = useState(null);
  const [retryTarget, setRetryTarget] = useState(null);
  const [dupeTarget, setDupeTarget] = useState(null);
  const [dupeMode, setDupeMode] = useState("dupe");
  const [banner, setBanner] = useState("");

  const sortedSeeds = useMemo(
    () => [...seeds].sort((a, b) => (b.created_at || 0) - (a.created_at || 0)),
    [seeds]
  );
  const sortedProjects = useMemo(
    () => [...projects].sort((a, b) => (b.created_at || 0) - (a.created_at || 0)),
    [projects]
  );
  const sortedVideos = useMemo(
    () => [...videos].sort((a, b) => (b.created_at || 0) - (a.created_at || 0)),
    [videos]
  );

  const handleSeedDelete = async (seedId) => {
    if (!window.confirm("Delete this seed?")) return;
    try {
      await deleteSeed(seedId);
    } catch (err) {
      setBanner(err.message || "Failed to delete seed");
    }
  };

  const handleSeedStar = async (seed) => {
    try {
      await toggleSeedStar(seed.id, !seed.starred);
    } catch (err) {
      setBanner(err.message || "Failed to update star");
    }
  };

  const handleProjectDelete = async (project) => {
    if (!window.confirm("Delete this project and all its versions?")) return;
    try {
      await deleteProject(project.id);
    } catch (err) {
      setBanner(err.message || "Failed to delete project");
    }
  };

  const handleVersionDelete = async (version) => {
    if (!window.confirm("Delete this version?")) return;
    try {
      await deleteVersion(version.id);
    } catch (err) {
      setBanner(err.message || "Failed to delete version");
    }
  };

  const handleVideoDelete = async (video) => {
    if (!window.confirm("Delete this video?")) return;
    try {
      await deleteVideo(video.id);
    } catch (err) {
      setBanner(err.message || "Failed to delete video");
    }
  };

  const handleVideoRetry = async (video) => {
    try {
      await retryVideo(video.id);
    } catch (err) {
      setBanner(err.message || "Failed to retry video");
    }
  };

  const handleDupeSubmit = ({ extraPrompt, actorUrl, cleanModel }) => {
    if (!dupeTarget) return;
    const sourceVideoId = dupeTarget.id;
    const mode = dupeMode;
    setDupeTarget(null);
    setDupeMode("dupe");
    setActiveTab("videos");

    const request =
      mode === "copy"
        ? copyVideo({
            sourceVideoId,
            cleanExtraPrompt: extraPrompt,
            cleanActorUrl: actorUrl,
            cleanModel,
          })
        : dupeVideo({
            sourceVideoId,
            cleanExtraPrompt: extraPrompt,
            cleanActorUrl: actorUrl,
            cleanModel,
          });

    request.catch((err) => {
      setBanner(
        err.message || (mode === "copy" ? "Failed to start copy" : "Failed to start dupe")
      );
    });
  };

  const handleSliceConfirm = async ({ start_seconds, end_seconds }) => {
    if (!sliceSeed) return;
    setInitialRemixSeed(sliceSeed);
    setInitialSlice({ start_seconds, end_seconds });
    setSliceSeed(null);
  };

  const handleInitialRemixSubmit = ({ extraPrompt, actorUrl, cleanModel }) => {
    if (!initialRemixSeed || !initialSlice) return;
    const seedId = initialRemixSeed.id;
    const startSeconds = initialSlice.start_seconds;
    const endSeconds = initialSlice.end_seconds;
    setInitialRemixSeed(null);
    setInitialSlice(null);
    setActiveTab("projects");
    setSliceBusy(true);
    createProject({
        seedId,
        startSeconds,
        endSeconds,
        cleanExtraPrompt: extraPrompt,
        cleanActorUrl: actorUrl,
        cleanModel,
      })
      .catch((err) => {
        setBanner(err.message || "Failed to create project");
      })
      .finally(() => {
        setSliceBusy(false);
      });
  };

  const handleRetry = async (version) => {
    if (version.pending_remix_prompt) {
      setRetryTarget(version);
    } else {
      try {
        await retryProject(version.id);
      } catch (err) {
        setBanner(err.message || "Failed to retry project");
      }
    }
  };

  const handleRetrySubmit = (version, quality) => {
    setRetryTarget(null);
    retryProject(version.id, quality).catch((err) => {
      setBanner(err.message || "Failed to retry project");
    });
  };

  const handleClone = (project) => {
    const seedId = project.seed_id;
    const slice = project.slice || project.versions?.[0]?.slice;

    if (!seedId || !slice) {
      setBanner("Cannot clone project: missing seed or slice information.");
      return;
    }

    setInitialRemixSeed({ id: seedId });
    setInitialSlice(slice);
  };

  if (loading) {
    return (
      <DashboardCanvas>
        <Shell>
          <p style={{ color: theme.colors.text.muted }}>Loading workspace...</p>
        </Shell>
      </DashboardCanvas>
    );
  }

  return (
    <DashboardCanvas>
      <Shell>
        <TopBar>
          <LogoLockup>
            <LogoMark src={logo} alt="CopyUGC" />
            <TitleStack>
              <strong>CopyUGC</strong>
              <span style={{ color: theme.colors.text.muted }}>Great Artists Steal</span>
            </TitleStack>
          </LogoLockup>

          <Tabs>
            {tabs.map((tab) => (
              <TabButton
                key={tab.id}
                active={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </TabButton>
            ))}
          </Tabs>
        </TopBar>

        {banner && (
          <Banner role="alert">
            {banner} <Button variant="ghost" onClick={() => setBanner("")}>Dismiss</Button>
          </Banner>
        )}

        {error && <Banner role="alert">{error}</Banner>}

        <ContentSection>
          {activeTab === "seeds" && (
            <SeedsPanel
              seeds={sortedSeeds}
              onCreateProject={(seed) => setSliceSeed(seed)}
              onToggleStar={handleSeedStar}
              onDeleteSeed={handleSeedDelete}
              busySeedId={sliceBusy && sliceSeed ? sliceSeed.id : null}
            />
          )}

          {activeTab === "projects" && (
            <ProjectsPanel
              projects={sortedProjects}
              onRemix={(version) => setRemixTarget(version)}
              onRender={(version) => setRenderTarget(version)}
              onDeleteVersion={handleVersionDelete}
              onDeleteProject={handleProjectDelete}
              onRetry={handleRetry}
              onClone={handleClone}
            />
          )}

          {activeTab === "videos" && (
            <VideosPanel
              videos={sortedVideos}
              onDelete={handleVideoDelete}
              onRetry={handleVideoRetry}
              onDupe={(video) => {
                setDupeMode("dupe");
                setDupeTarget(video);
              }}
              onCopy={(video) => {
                setDupeMode("copy");
                setDupeTarget(video);
              }}
            />
          )}

          {activeTab === "actors" && <ActorsPanel onBanner={setBanner} />}
        </ContentSection>
      </Shell>

      {activeTab !== "actors" && (
        <StickyButton onClick={() => setSubmitOpen(true)}>
          <FiPlus /> Add seed
        </StickyButton>
      )}

      <Modal isOpen={isSubmitOpen} onClose={() => setSubmitOpen(false)} title="Add TikTok seed">
        <SubmitModal onSuccess={() => setSubmitOpen(false)} />
      </Modal>

      <SeedSliceDialog
        isOpen={Boolean(sliceSeed)}
        seed={sliceSeed}
        busy={sliceBusy}
        onClose={() => (sliceBusy ? null : setSliceSeed(null))}
        onConfirm={handleSliceConfirm}
      />

      <InitialRemixDialog
        isOpen={Boolean(initialRemixSeed)}
        seed={initialRemixSeed}
        slice={initialSlice}
        loading={sliceBusy}
        onSubmit={handleInitialRemixSubmit}
        onClose={() => (sliceBusy ? null : (setInitialRemixSeed(null), setInitialSlice(null)))}
      />

      <RemixDialog
        isOpen={Boolean(remixTarget)}
        project={remixTarget}
        onClose={() => setRemixTarget(null)}
      />

      <RenderDialog
        isOpen={Boolean(renderTarget)}
        project={renderTarget}
        onClose={() => setRenderTarget(null)}
        onModify={(proj) => setModifyTarget(proj)}
      />

      <ModifyPromptDialog
        isOpen={Boolean(modifyTarget)}
        project={modifyTarget}
        onClose={() => setModifyTarget(null)}
      />

      <RetryDialog
        isOpen={Boolean(retryTarget)}
        version={retryTarget}
        onClose={() => setRetryTarget(null)}
        onSubmit={handleRetrySubmit}
      />

      <VideoDupeDialog
        isOpen={Boolean(dupeTarget)}
        video={dupeTarget}
        loading={false}
        mode={dupeMode}
        onClose={() => {
          setDupeTarget(null);
          setDupeMode("dupe");
        }}
        onSubmit={handleDupeSubmit}
      />
    </DashboardCanvas>
  );
}

export default Dashboard;
